<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import Listbox from 'primevue/listbox';
import Message from 'primevue/message';
import Popover from 'primevue/popover';
import { useOpportunityCreationStore } from '@apparatix/stores/useOpportunityCreationStore';
import { useOpportunityStore } from '@apparatix/stores/useOpportunityStore';
import { useStringFunctions, useContacts } from '@apparatix/composables';
import { AssociationContact, ContactSearchResult, ContactLabel } from '@apparatix/types/Leads';
import type { ContactPayload, Address } from '@apparatix/types/Contacts';
import LeadWizardContactForm from '@apparatix/leads/LeadWizardContactForm.vue';
import { wizardNewContact, type WizardNewContact, newContactPayloadTransformer } from '@apparatix/schemas/CreateLeadWizard';
import { createObjectFromSchema } from '@apparatix/schemas/Helpers';
import { PendingContact } from '@apparatix/schemas/Contact';
import { ContactCard } from '@apparatix/leads/ContactCard';
import { Stack } from '@apparatix/library/core';
import MultiSelect from '@apparatix/library/MultiSelect.vue';
import Tag from 'primevue/tag';

const store = useOpportunityCreationStore();
const opportunityStore = useOpportunityStore();
const { isEmptyString } = useStringFunctions();
const {
  createContact, mapSearchResultToAssociation, getContactLabelChipSeverity,
} = useContacts();

const selectedContact = ref<ContactSearchResult>();
const activeContact = ref<AssociationContact>();

const props = withDefaults(
  defineProps<{
    address: Address
    companyName: string
  }>(),
  {
    address: () => null,
    companyName: () => null,
  },
);

watch(() => selectedContact.value, newValue => {
  if (!newValue || typeof newValue !== 'object') return;
  store.addContact(mapSearchResultToAssociation(newValue));
  selectedContact.value = undefined;
});

const getNewContactSchema = () => wizardNewContact;
const contactFormModel = ref<WizardNewContact>(createObjectFromSchema(getNewContactSchema()));
const contactHasUnsavedEdits = ref(false);
const refreshContactModal = ref(0);

const addPendingContact = async (payload: PendingContact) => {
  let contact: AssociationContact;
  if (!payload.id) {
    const contactPayload = newContactPayloadTransformer.parse(payload);
    const result = await createContact(contactPayload as ContactPayload);

    if (!result?.id) {
      throw new Error(`Error saving ${payload.fullName} as new Contact`);
    }

    contact = {
      id: result.id,
      firstName: result.contact.first_name,
      lastName: result.contact.last_name,
      contactLabels: [],
      companyName: result.contact.company_name,
      c2Name: result.contact.company_name,
    };
  } else {
    contact = {
      id: payload.id,
      firstName: payload.firstName,
      lastName: payload.lastName,
      contactLabels: [],
      companyName: payload.companyName,
      c2Name: payload.companyName,
    };
  }


  if (payload.emails?.length) {
    contact.addressEmails = payload.emails;
  }

  if (payload.phoneNumbers?.length) {
    contact.addressPhoneNumbers = payload.phoneNumbers;
  }

  store.addContact(contact);
  contactFormModel.value = createObjectFromSchema(getNewContactSchema());

  refreshContactModal.value += 1;
};

const onContactFormCancel = () => contactFormModel.value = createObjectFromSchema(getNewContactSchema());

const shouldDisableContactLabel = (label: ContactLabel) => {
  // If it isn't the primary label or bill to allow multiple.
  if (label.contactLabelId !== 1 && label.contactLabelId !== 3) {
    return false;
  }

  const usedLabels = contactsExcludingActive.value.flatMap(contact => contact.contactLabels);
  const idx = usedLabels.findIndex(l => l.contactLabelId === label.contactLabelId);
  return idx !== -1;
};

const contactsExcludingActive = computed(() => store.contacts.filter(contact => contact.id !== activeContact.value.id));

const activeContactLabels = computed({
  get() {
    if (!activeContact.value) return undefined;
    const contactIndex = store.contacts.findIndex(ct => ct.id === activeContact.value.id);

    return store.contacts[contactIndex].contactLabels;
  },
  set(value: ContactLabel[]) {
    if (!activeContact.value) return;
    const contactIndex = store.contacts.findIndex(ct => ct.id === activeContact.value.id);
    store.contacts[contactIndex].contactLabels = value;
  },
});

const getContactTitle = (contact: AssociationContact) => {
  let title = '';
  if (!isEmptyString(contact.firstName)) {
    title = contact.firstName;
  }
  if (!isEmptyString(contact.lastName) && !isEmptyString(contact.firstName)) {
    title = `${title} ${contact.lastName}`;
  } else if (!isEmptyString(contact.lastName) && isEmptyString(contact.firstName)) {
    title = contact.lastName;
  }

  if (!isEmptyString(title)) {
    return title;
  }

  return 'Unnamed Contact';
};

const getContactSubtitle = (contact: AssociationContact) => {
  if (!isEmptyString(contact.jobTitle)) {
    return contact.jobTitle;
  }

  return contact.c2Name ?? ' ';
};

const removeContact = (remove: WizardNewContact) => {
  store.contacts = store.contacts.filter(contact => contact.id !== remove.id);
};

</script>
<template>
  <div class="flex flex-col lg:flex-row-reverse gap-4 lg:gap-x-6 w-full">
    <div class="flex-initial flex flex-col gap-3 w-full lg:w-1/2">
      <h2 class="font-medium text-sm text-[var(--swimlane-secondary-text)]">
        Contacts
      </h2>
      <div v-if="store.contacts.length" class="flex flex-col gap-4">
        <ContactCard
          v-for="contact in store.contacts"
          :id="contact.id"
          :key="contact.id"
          :name="getContactTitle(contact)"
          :company="getContactSubtitle(contact)"
          :owner="contact.ownerName"
          :closable="true"
          @on-close="removeContact(contact)"
        >
          <Stack :gap="2">
            <div class="flex items-center">
              <MultiSelect
                v-model="contact.contactLabels"
                :options="opportunityStore.options['Lead'].contactLabels"
                option-label="contactLabelDisplayName"
                placeholder="Select Contact Type"
                show-chips
                size="small"
                :show-toggle-all="false"
                fluid
                :option-disabled="shouldDisableContactLabel"
                @before-show="() => (activeContact = contact)"
              >
                <template #chips="slotProps">
                  <Tag :severity="getContactLabelChipSeverity(slotProps.value)" :value="slotProps.value.contactLabelDisplayName" />
                </template>
              </MultiSelect>
            </div>
          </Stack>
        </ContactCard>
      </div>
      <template v-if="!store.isLoading">
        <Message v-for="(error, idx) in store.errorBag.contacts" :key="idx" severity="error">
          {{ error }}
        </Message>
      </template>
    </div>
    <div class="flex-initial flex flex-col gap-4 w-full lg:w-1/2">
      <LeadWizardContactForm
        v-model="contactFormModel"
        :use-dynamic-company-search="false"
        :use-dynamic-name-search="true"
        :is-associated-with-existing-account="false"
        :allow-agency-in-wizard="false"
        :address="props.address"
        :company-name="props.companyName"
        :restore-prefilled-fields="refreshContactModal"
        @on:create="addPendingContact"
        @contact-selected="addPendingContact"
        @cancel="onContactFormCancel"
        @on:dirty-change="dirty => contactHasUnsavedEdits = dirty"
      />
    </div>
  </div>
  <Popover ref="typeSelectPopover">
    <div class="flex w-40">
      <Listbox
        v-model="activeContactLabels"
        class="w-full"
        :options="opportunityStore.options['Lead'].contactLabels"
        :option-disabled="shouldDisableContactLabel"
        option-label="contactLabelDisplayName"
        multiple
      />
    </div>
  </Popover>
</template>
